import Logger from "neon-frontend-utils/src/logger";
import Components from "../../../../../constants/Components";
import {
    getCollectionOfSelectedFilterItems,
    isParentOrganisationSelected,
} from "../../../utils/helperFunctionsForSelectedFilterItems";
import * as actionType from "../constants/ActionTypes";
import { FilterDateSectionTypes } from "../constants/FilterDateSectionTypes";
import { FilterSectionTypes } from "../constants/FilterSectionTypes";

const initialState = {
    consultants: {
        filteredList: [],
        list: [],
        title: "",
        type: "",
    },
    costHeadings: {
        filteredList: [],
        list: [],
        title: "",
        type: "",
    },
    organisations: {
        filteredList: [],
        list: [],
        title: "",
        type: "",
    },
    products: {
        list: [],
        title: "",
        type: "",
    },
    projects: {
        filteredList: [],
        list: [],
        title: "",
        type: "",
    },
    selectedFilterItems: {
        [FilterSectionTypes.organisations]: [],
        [FilterSectionTypes.projects]: [],
        [FilterSectionTypes.products]: [],
        [FilterSectionTypes.status]: [],
        [FilterSectionTypes.planningStatus]: [],
        [FilterSectionTypes.outcome]: [],
        [FilterDateSectionTypes.completedOn]: [],
        [FilterDateSectionTypes.created]: [],
        [FilterDateSectionTypes.appointment]: [],
        [FilterSectionTypes.consultants]: [],
        [FilterSectionTypes.costHeading]: [],
    },
    selectedFilterItemsForHeader: [],
    status: {
        list: [],
        title: "",
        type: "",
    },
};

/**
 * Returns the new state
 * @param {Object} state - state
 * @param {Object} action - action
 * @returns {{}} new state
 */
export default function participantsFilterReducer(state = initialState, action) {
    const newState = Object.assign({}, state);

    const getSelectedFilterItemsForHeader = () => {
        let selectedFilterItems;

        if (!newState.organisations.list.length) {
            selectedFilterItems = getCollectionOfSelectedFilterItems(newState.selectedFilterItems).filter(
                (item) => item.type !== FilterSectionTypes.organisations,
            );
        } else {
            selectedFilterItems = getCollectionOfSelectedFilterItems(newState.selectedFilterItems);
        }

        return selectedFilterItems;
    };

    const getFilterItemsBySelectedParent = (data) => {
        const check = isParentOrganisationSelected;

        const allData = data;
        const selectedFilterItems = getCollectionOfSelectedFilterItems(newState.selectedFilterItems);
        const filteredSelectedItems = [];

        selectedFilterItems.forEach((item) => {
            if (check(selectedFilterItems, item)) {
                filteredSelectedItems.push(item);
            }
        });

        const result = [];

        filteredSelectedItems.forEach((selectedItem) => {
            allData.forEach((item) => {
                if (selectedItem.slug === item.parentSlug) {
                    result.push(item);
                }
            });
        });

        return result;
    };

    const handleChildOrganisations = (childOrganisations) => {
        if (!childOrganisations) {
            return null;
        }
        return childOrganisations.map((organisation) => ({
            children: handleChildOrganisations(organisation.children),
            id: organisation.id,
            name: organisation.organisationName,
            parentId: organisation.parentId || null,
            slug: organisation.organisationSlug,
        }));
    };

    switch (action.type) {
        case actionType.GET_ORGANISATIONS_FOR_FILTER: {
            try {
                if (action.response) {
                    const organisationList = action.response.map((organisation) => ({
                        children: handleChildOrganisations(organisation.children),
                        id: organisation.id,
                        name: organisation.organisationName,
                        parentId: organisation.parentId || null,
                        slug: organisation.organisationSlug,
                    }));

                    if (organisationList.length === 1) {
                        newState.selectedFilterItems[FilterSectionTypes.organisations] = organisationList;
                    } else {
                        newState.organisations = {
                            filteredList: organisationList,
                            list: organisationList,
                            title: action.title,
                            type: FilterSectionTypes.organisations,
                        };
                    }
                }
            } catch (error) {
                Logger.instance.error({
                    component: Components.PARTICIPANTS_FILTER,
                    error,
                    message: "Exception in participantsFilterReducer reducer GET_ORGANISATIONS_FOR_FILTER",
                });
            }

            break;
        }

        case actionType.GET_CONSULTANTS_FOR_FILTER: {
            try {
                if (action.response) {
                    const consultantsList = action.response.map((consultant) => ({
                        accountHasRoleId: consultant.accountHasRoleId,
                        id: consultant.id,
                        name: consultant.fullName,
                        slug: consultant.accountHasRoleUuid,
                    }));

                    newState.consultants = {
                        filteredList: consultantsList,
                        list: consultantsList,
                        title: action.title,
                        type: FilterSectionTypes.consultants,
                    };
                } else {
                    newState.consultants = {
                        ...newState.consultants,
                        filteredList: [],
                        list: [],
                    };

                    newState.selectedFilterItems[FilterSectionTypes.consultants] = [];
                    newState.selectedFilterItemsForHeader = getSelectedFilterItemsForHeader();
                }
            } catch (error) {
                Logger.instance.error({
                    component: Components.PARTICIPANTS_FILTER,
                    error,
                    message: "Exception in participantsFilterReducer reducer GET_CONSULTANTS_FOR_FILTER",
                });
            }

            break;
        }

        case actionType.GET_COST_HEADINGS_FOR_FILTER: {
            try {
                if (action.response) {
                    const costHeadings = action?.response
                        .filter((cost) => typeof cost === "string" && cost.length > 0)
                        .map((cost) => ({ id: cost, name: cost, slug: cost }));

                    newState.costHeadings = {
                        filteredList: costHeadings,
                        list: costHeadings,
                        title: action.title,
                        type: FilterSectionTypes.costHeading,
                    };
                } else {
                    newState.costHeadings = {
                        ...newState.costHeadings,
                        filteredList: [],
                        list: [],
                    };

                    newState.selectedFilterItems[FilterSectionTypes.costHeading] = [];
                    newState.selectedFilterItemsForHeader = getSelectedFilterItemsForHeader();
                }
            } catch (error) {
                Logger.instance.error({
                    component: Components.PARTICIPANTS_FILTER,
                    error,
                    message: "Exception in participantsFilterReducer reducer GET_COST_HEADINGS_FOR_FILTER",
                });
            }

            break;
        }

        case actionType.GET_PRODUCTS_FOR_FILTER: {
            try {
                if (action.response) {
                    const productList = action.response.map((product) => ({
                        id: product.productId,
                        name: action.i18n[`cp_participants_${product.translationKey}`] || product.productName,
                        slug: product.productSlug,
                    }));

                    newState.products = {
                        list: productList,
                        title: action.title,
                        type: FilterSectionTypes.products,
                    };
                } else {
                    newState.products = {
                        ...newState.products,
                        filteredList: [],
                        list: [],
                    };

                    newState.selectedFilterItems[FilterSectionTypes.products] = [];
                    newState.selectedFilterItemsForHeader = getSelectedFilterItemsForHeader();
                }
            } catch (error) {
                Logger.instance.error({
                    component: Components.PARTICIPANTS_FILTER,
                    error,
                    message: "Exception in participantsFilterReducer reducer GET_PRODUCTS_FOR_FILTER",
                });
            }

            break;
        }
        case actionType.GET_STATUS_FOR_FILTER: {
            try {
                newState.status = {
                    list: action.response,
                    title: action.title,
                    type: FilterSectionTypes.status,
                };
            } catch (error) {
                Logger.instance.error({
                    component: Components.PARTICIPANTS_FILTER,
                    error,
                    message: "Exception in participantsFilterReducer reducer GET_STATUS_FOR_FILTER",
                });
            }

            break;
        }

        case actionType.GET_PROJECTS_FOR_FILTER: {
            try {
                if (action.response) {
                    const filteredProjects = getFilterItemsBySelectedParent(action.response);

                    const projectsList = filteredProjects.map((project) => ({
                        deactivatedOn: project.deactivatedOn || null,
                        id: project.projectId,
                        name: project.projectName,
                        parentSlug: project.parentSlug,
                        slug: project.projectSlug,
                    }));

                    newState.projects = {
                        filteredList: projectsList,
                        list: projectsList,
                        title: action.title,
                        type: FilterSectionTypes.projects,
                    };
                } else {
                    newState.projects = {
                        ...newState.projects,
                        filteredList: [],
                        list: [],
                    };

                    newState.selectedFilterItems[FilterSectionTypes.projects] = [];
                    newState.selectedFilterItemsForHeader = getSelectedFilterItemsForHeader();
                }
            } catch (error) {
                Logger.instance.error({
                    component: Components.PARTICIPANTS_FILTER,
                    error,
                    message: "Exception in participantsFilterReducer reducer GET_PROJECTS_FOR_FILTER",
                });
            }

            break;
        }

        case actionType.GET_SELECTED_ITEMS_FOR_FILTER: {
            try {
                if (action.response) {
                    newState.selectedFilterItems = action.response;
                    newState.selectedFilterItemsForHeader = getSelectedFilterItemsForHeader();
                }
            } catch (error) {
                Logger.instance.error({
                    component: Components.PARTICIPANTS_FILTER,
                    error,
                    message: "Exception in participantsFilterReducer reducer GET_SELECTED_ITEMS_FOR_FILTER",
                });
            }

            break;
        }

        case actionType.FILTER_SECTION_BY_VALUE_FOR_FILTER: {
            try {
                const value = action.value;
                const sectionType = action.sectionType;

                // Convert input value string and list items with normalize to match characters on search
                // ÁÉÍÓÚáéíóúâêîôûàèìòùÇç -> AEIOUaeiouaeiouaeiouCc
                const convertedCharValue = value.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
                const convertedCharList = newState[sectionType].list.map((item) => ({
                    id: item.id,
                    name: item.name.normalize("NFD").replace(/[\u0300-\u036f]/g, ""),
                    slug: item.slug,
                }));

                const convertedCharFilteredList = convertedCharList.filter((item) =>
                    item.name.toLowerCase().includes(convertedCharValue.toLowerCase()),
                );
                const filteredList = newState[sectionType].list.filter((a) =>
                    convertedCharFilteredList.some((b) => a.slug === b.slug),
                );

                newState[sectionType] = {
                    ...newState[sectionType],
                    filteredList,
                };
            } catch (error) {
                Logger.instance.error({
                    component: Components.PARTICIPANTS_FILTER,
                    error,
                    message: "Exception in participantsFilterReducer reducer FILTER_SECTION_BY_VALUE_FOR_FILTER",
                });
            }

            break;
        }

        default:
            return state;
    }

    return newState;
}
